import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Alert, Button, DatePicker, Spin} from 'antd';
import moment from 'moment';
import {displayAmount, roundAmount} from "../utils/money";
import MoneyDisplay from "../common/MoneyDisplay";
import axios from "axios";
import {authorizationHeaders} from "../utils/api";
import "./GrossProfitReportPage.css";
import {defaultComparer} from "../utils/compare";
import SelectCategoryTree from "../Category/SelectCategoryTree";
import {bindActionCreators} from "redux";
import {getCategoryTree} from "../Category/actions";
import {getCategoryAndChildrenIds} from "../Category/tree";

const maxResults = 10000;

const styles = {
  floatRight: {
    float: 'right',
  },
};

export class GrossProfitReportPage extends Component {
  static propTypes = {
    /* state */
    dispensaryId: PropTypes.string.isRequired,
    categoryTree: PropTypes.object.isRequired,
    /* dispatch */
    getCategoryTree: PropTypes.func.isRequired,
  };

  state = {
    dateTimeFrom: moment().startOf('day'),
    dateTimeTo: moment().endOf('day'),
    loading: false,
    data: null,
    error: '',
    categoryId: '',
  };

  componentDidMount() {
    this._loadCategories();
    this._loadData();
  }

  componentDidUpdate(prevProps, prevState) {
    const {dispensaryId} = this.props;
    if (dispensaryId !== prevProps.dispensaryId) {
      this._loadCategories();
      this._loadData();
    }
    if (this.state.categoryId !== prevState.categoryId) {
      this._loadData();
    }
  }

  _loadCategories() {
    const {dispensaryId, getCategoryTree} = this.props;
    getCategoryTree({dispensaryId});
  }

  _loadData() {
    const filter = {
      where: {
        dispensaryId: {eq: this.props.dispensaryId},
        datetime: {between: [this.state.dateTimeFrom, this.state.dateTimeTo]},
      },
      limit: maxResults,
    };
    if (this.state.categoryId) {
      const categoryIds = getCategoryAndChildrenIds(this.props.categoryTree, this.state.categoryId);
      filter.where.categoryId = {inq: categoryIds};
    }
    this.setState({loading: true, error: ''});
    axios.get(
      `${process.env.PUBLIC_URL}/api/v1/r/grossProfitReport?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`,
      {headers: authorizationHeaders()}
    ).then(res => this._processData(res.data), err => this.setState({error: err.message}))
      .finally(() => this.setState({loading: false}));
  }

  _processData(data) {
    const {
      rowsByCategoryId, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin,
    } = data.reduce(
      (
        {rowsByCategoryId, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin},
        {productId, productName, quantity, salePrice, discountRate, costPrice, categoryId, categoryName}
      ) => {
        const category = rowsByCategoryId[categoryId] || (rowsByCategoryId[categoryId] = {
          rowsByProductId: {}, totalQuantity: 0, totalReturns: 0, totalRevenue: 0, totalCostOfGoods: 0,
          totalGrossProfit: 0, totalMargin: 0, categoryId, categoryName,
        });
        const product = category.rowsByProductId[productId] || (category.rowsByProductId[productId] = {
          productName, quantity: 0, returns: 0, revenue: 0, costOfGoods: 0, grossProfit: 0, margin: 0, productId,
        });
        if (quantity > 0) {
          product.quantity += quantity;
          category.totalQuantity += quantity;
          totalQuantity += quantity;
        } else {
          product.returns += -quantity;
          category.totalReturns += -quantity;
          totalReturns += -quantity;
        }
        const revenue = roundAmount(roundAmount(quantity * salePrice) * (1 - discountRate));
        product.revenue += revenue;
        category.totalRevenue += revenue;
        totalRevenue += revenue;
        const costOfGoods = roundAmount(quantity * costPrice);
        product.costOfGoods += costOfGoods;
        category.totalCostOfGoods += costOfGoods;
        totalCostOfGoods += costOfGoods;
        product.grossProfit = product.revenue - product.costOfGoods;
        category.totalGrossProfit = category.totalRevenue - category.totalCostOfGoods;
        totalGrossProfit = totalRevenue - totalCostOfGoods;
        product.margin = roundAmount((product.grossProfit / product.revenue) * 100);
        category.totalMargin = roundAmount((category.totalGrossProfit / category.totalRevenue) * 100);
        totalMargin = roundAmount((totalGrossProfit / totalRevenue) * 100);
        return {
          rowsByCategoryId, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin,
        };
      },
      {
        rowsByCategoryId: {},
        totalQuantity: 0,
        totalReturns: 0,
        totalRevenue: 0,
        totalCostOfGoods: 0,
        totalGrossProfit: 0,
        totalMargin: 0,
      }
    );
    const categories = Object.values(rowsByCategoryId)
      .sort(({categoryName: a}, {categoryName: b}) => defaultComparer(a, b))
      .map(({rowsByProductId, ...rest}) => ({
        rows: Object.values(rowsByProductId).sort(({productName: a}, {productName: b}) => defaultComparer(a, b)),
        ...rest
      }));
    this.setState({data: {
      categories, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin,
      count: data.length,
    }});
  }

  _onCategoryChange = (categoryId) => {
    this.setState({categoryId});
  };

  _onRangePickerChange = (values) => {
    const [dateTimeFrom, dateTimeTo] = values;
    if (!dateTimeFrom || !dateTimeTo) return;
    this.setState({dateTimeFrom, dateTimeTo});
  };

  _print = () => window.print();

  render() {
    const {dateTimeFrom, dateTimeTo, loading, data, categoryId} = this.state;
    const {
      categories, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin,
    } = data || {
      categories: [], totalQuantity: 0, totalReturns: 0, totalRevenue: 0, totalCostOfGoods: 0, totalGrossProfit: 0,
      totalMargin: 0,
    };
    return (
      <div>
        <div className="transaction-history-report-form">
          <Button icon="printer" style={styles.floatRight} onClick={this._print}>Print</Button>
          <DatePicker.RangePicker
            showTime={{defaultValue: [moment('00:00:00', 'HH:mm:ss'), moment('11:59:59', 'HH:mm:ss')]}}
            format="YYYY-MM-DD HH:mm:ss"
            value={[dateTimeFrom, dateTimeTo]}
            onChange={this._onRangePickerChange}
            onOk={() => this._loadData()}
          />
          <SelectCategoryTree
            tree={this.props.categoryTree}
            showAll
            value={categoryId}
            onChange={this._onCategoryChange}
            style={{minWidth: '300px'}}
          />
          {this._renderAlert()}
        </div>
        <Spin spinning={loading}>
          <h1>Gross Profit</h1>
          <p>
            <strong>Period:</strong> {dateTimeFrom.format('MMMM Do YYYY, h:mm:ss a')} to {dateTimeTo.format('MMMM Do YYYY, h:mm:ss a')}
          </p>
          <table id="grossProfitReportTable">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Returns</th>
                <th>Revenue</th>
                <th>Cost of Goods</th>
                <th>Gross Profit</th>
                <th>Margin</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(({rows, categoryId, categoryName, totalQuantity, totalReturns, totalRevenue, totalCostOfGoods, totalGrossProfit, totalMargin}) =>
                <React.Fragment key={categoryId}>
                  {rows.map(({productId, productName, quantity, returns, revenue, costOfGoods, grossProfit, margin}) =>
                    <tr key={productId}>
                      <td>{productName}</td>
                      <td>{quantity}</td>
                      <td>{returns}</td>
                      <td><MoneyDisplay amount={revenue}/></td>
                      <td><MoneyDisplay amount={costOfGoods}/></td>
                      <td><MoneyDisplay amount={grossProfit}/></td>
                      <td>{displayAmount(margin, 2, '')}%</td>
                    </tr>
                  )}
                  <tr>
                    <th>{categoryName}</th>
                    <th>{totalQuantity}</th>
                    <th>{totalReturns}</th>
                    <th><MoneyDisplay amount={totalRevenue}/></th>
                    <th><MoneyDisplay amount={totalCostOfGoods}/></th>
                    <th><MoneyDisplay amount={totalGrossProfit}/></th>
                    <th>{displayAmount(totalMargin, 2, '')}%</th>
                  </tr>
                </React.Fragment>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th>Total</th>
                <th>{totalQuantity}</th>
                <th>{totalReturns}</th>
                <th><MoneyDisplay amount={totalRevenue}/></th>
                <th><MoneyDisplay amount={totalCostOfGoods}/></th>
                <th><MoneyDisplay amount={totalGrossProfit}/></th>
                <th>{displayAmount(totalMargin, 2, '')}%</th>
              </tr>
            </tfoot>
          </table>
        </Spin>
      </div>
    );
  }

  _renderAlert() {
    const {error, data} = this.state;
    let type, message, description;
    if (error) {
      type = 'error';
      message = 'Error';
      description = error;
    } else if (!data || data.count === 0) {
      type = 'info';
      message = "No data found";
      description = "Choose a different date range";
    } else if (data.count === maxResults) {
      type = 'warning';
      message = "Too many data";
      description = "Choose a smaller date range";
    } else {
      return null;
    }
    return (<Alert type={type} message={message} description={description}/>);
  }
}

const mapStateToProps = (state) => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  categoryTree: state.category.categoryTree,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({getCategoryTree}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(GrossProfitReportPage);



// WEBPACK FOOTER //
// ./src/Reports/GrossProfitReportPage.js